import { useCallback, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { toTitleCase } from '../lib/format'
import { artGradient } from '../lib/songColour'

/**
 * A swipeable rail of songs.
 *
 * Two things make it a feed rather than a list that happens to be sideways:
 *
 * The peek. Card width is a fraction of the rail, not a fixed pixel size, so
 * the third card is always cut by the right edge whatever the screen is.
 * A row that ends flush reads as "that's all of them"; a row cut mid-card
 * reads as "there's more", and the thumb follows it. Fixed-width cards get
 * this right on exactly one phone and land flush on the next one.
 *
 * The loop. Reaching the end of someone's catalogue is a dead stop, and a
 * dead stop is where people leave. The list is rendered three times and the
 * scroll position is silently moved back one copy whenever it drifts into
 * the outer thirds, so the rail never has an end to hit. The jump is exactly
 * one copy-width, which is a position the rail was already showing — nothing
 * moves on screen, and it lands on the same snap point it left.
 *
 * Only loops from three songs up. Below that the duplicates are obvious —
 * you'd be swiping past the same two cards — and a short rail that simply
 * stops is the honest thing.
 */
export default function SongRail({ songs, to, meta, badge }) {
  const el = useRef(null)
  const loops = songs.length >= 3
  const items = loops ? [...songs, ...songs, ...songs] : songs

  /**
   * The width of one copy, measured off the cards themselves.
   *
   * Not scrollWidth / 3 — that includes the rail's own padding, so it comes
   * out a few pixels short of the real pitch, and a jump of the wrong length
   * lands between snap points. Proximity snap then nudges it, the next jump
   * inherits the error, and the rail slowly walks out of alignment. The
   * distance between a card and its own duplicate is exact by construction.
   */
  const copyWidth = () => {
    const n = el.current
    const cards = n?.children
    if (!cards || cards.length < songs.length * 2) return 0
    return cards[songs.length].offsetLeft - cards[0].offsetLeft
  }

  // Start in the middle copy so the first swipe can go either way.
  useEffect(() => {
    const n = el.current
    if (!n || !loops) return
    n.scrollLeft = copyWidth()
    // copyWidth reads the DOM this effect's own render just produced.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loops, songs.length])

  const onScroll = useCallback(() => {
    const n = el.current
    if (!n || !loops) return
    const copy = copyWidth()
    if (!copy) return
    // Half a copy of slack either side: far enough from both ends that the
    // correction never happens under the finger at the edge of a swipe.
    if (n.scrollLeft < copy * 0.5) n.scrollLeft += copy
    else if (n.scrollLeft > copy * 1.5) n.scrollLeft -= copy
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loops, songs.length])

  return (
    <div className="songcards" ref={el} onScroll={onScroll}>
      {items.map((s, i) => {
        // Copies exist for the eye, not for the screen reader or the tab
        // order — one pass through the songs is the real content.
        const ghost = loops && (i < songs.length || i >= songs.length * 2)
        return (
          <Link key={`${s.id}-${i}`} className="songcard" to={to(s)}
                style={{ background: artGradient(s.id) }}
                aria-hidden={ghost || undefined} tabIndex={ghost ? -1 : undefined}>
            {badge && <span className="songcard-badge">{badge(s)}</span>}
            <b>{toTitleCase(s.title) || 'Untitled'}</b>
            {meta?.(s) && <small>{meta(s)}</small>}
          </Link>
        )
      })}
    </div>
  )
}
